import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Observable, filter } from 'rxjs';
import { UserProfile } from 'shared/models';
import { UsersAPIService } from 'shared/services';
import { AlertService, DialogService } from 'shared/ui';
import {
  EditProfileComponent,
  FollowerComponent,
  FollowingComponent,
} from 'user';

import { environment } from '../../../environments/environment';

import { UserService } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import {
  AppSettings,
  AppSettingsStoreService,
} from '../../services/app-settings-store.service';
import { NativeEnvironmentService } from '../../services/native-environment.service';
import { APIService } from 'viewer';
import { lt } from 'semver';

@Component({
  templateUrl: './settings-main-page.component.html',
  styleUrl: './settings-main-page.component.scss',
})
export class SettingsMainPageComponent implements OnInit {
  @ViewChild('backupInput') backupInputElem?: ElementRef<HTMLInputElement>;

  public _userName?: string;
  public _userNickname?: string;
  public _userEmail?: string;
  public _userProfileImage?: string;

  public _profile$?: Observable<UserProfile>;

  public _appVersion: string;
  public _webAppVersion: string;

  public _settings?: AppSettings;
  public _isExternalPageOpening = false;
  public _isApp = false;

  constructor(
    private _userService: UserService,
    private _usersAPIService: UsersAPIService,
    private _dialogService: DialogService,
    private _alertService: AlertService,
    private _authService: AuthService,
    private _appSettingsStoreService: AppSettingsStoreService,
    private _nativeEnvironmentService: NativeEnvironmentService,
    private _apiService: APIService
  ) {
    this._appVersion = this._nativeEnvironmentService.version ?? '';
    this._webAppVersion = environment.appVersion;
  }

  ngOnInit(): void {
    this._isApp = !!(
      (window as any).webkit?.messageHandlers?.bukHybrid ||
      (window as any).bukAndroid
    );

    this._appSettingsStoreService.settings$.subscribe((settings) => {
      this._settings = settings;
    });

    this._userEmail = this._userService.getUser()?.email;
    this._userName = this._userService.getUser()?.name;
    this._userNickname = this._userService.getUser()?.nickname;
    this._userProfileImage = this._userService.getUser()?.avatar_image_url;
    this._getProfile();
  }

  private _getProfile(): void {
    this._profile$ = this._usersAPIService.getUsersProfile();
  }

  _onFollowersClick(): void {
    this._dialogService
      .open(FollowerComponent)
      .afterClosed()
      .pipe(filter((result) => !!result && result.updated))
      .subscribe(() => {
        this._getProfile();
      });
  }

  _onFollowingsClick(): void {
    this._dialogService
      .open(FollowingComponent)
      .afterClosed()
      .pipe(filter((result) => !!result && result.updated))
      .subscribe(() => {
        this._getProfile();
      });
  }

  _onEditProfileButtonClick(): void {
    this._dialogService
      .open(EditProfileComponent, {
        data: {
          initial: false,
          profileImage: this._userProfileImage,
          profileName: this._userNickname,
        },
      })
      .afterClosed()
      .pipe(filter((result) => !!result && !result.canceled))
      .subscribe((result) => {
        if (result && !result.canceled) {
          this._userProfileImage = result.profileImage || '';
          this._userNickname = result.profileName || '';
          this._getProfile();
        }
      });
  }

  _onLogoutButtonClick(): void {
    this._alertService
      .openConfirm('', '로그아웃 하겠습니까?', '로그아웃')
      .afterClosed()
      .pipe(filter((ok) => !!ok))
      .subscribe(() => {
        this._authService.logout(location.origin);
      });
  }

  _onTermsOfServiceButtonClick(): void {
    this._openExternalPageWithAppBridge(
      `${environment.serviceOrigin}/@ca7841/102.0.24.200`
    );
  }

  _onPrivacyPolicyButtonClick(): void {
    this._openExternalPageWithAppBridge(
      `${environment.serviceOrigin}/@ca7841/102.0.24.229`
    );
  }

  _onContactButtonClick(): void {
    this._openExternalPageWithAppBridge(
      `${environment.serviceOrigin}/help/contact`
    );
  }

  _onDeleteAccountButtonClick(): void {
    window.open(`${environment.serviceOrigin}/account/info`, '_blank');
  }

  _onSettingsChange<T extends keyof AppSettings>(
    key: T,
    value: AppSettings[T]
  ): void {
    this._appSettingsStoreService.update(key, value);
  }

  _onBackupDrawingSettingsChange(isOn: boolean): void {
    if (!isOn) {
      this._alertService
        .openConfirm(
          undefined,
          'OFF 상태로 변경 시, 알 수 없는 경로로 필기 유실 문제가 발생한 경우 북이오 서버에 자동 백업된 필기 기록 데이터로 복원이 불가합니다. 상태를 변경하시겠습니까?'
        )
        .afterClosed()
        .subscribe((result) => {
          if (result) {
            this._onSettingsChange('backupDrawing', isOn);
          } else {
            this.backupInputElem!.nativeElement.checked = true;
          }
        });
    } else {
      this._onSettingsChange('backupDrawing', isOn);
    }
  }

  // 1. 브릿지 존재 여부를 확인하는 헬퍼 함수 (전역 또는 클래스 내부)
  private checkBridgeAvailable(): boolean {
    const appWin = window as any;
    const hasAndroidBridge =
      typeof appWin.bukAndroid?.openBrowser === 'function';
    const hasIosBridge =
      typeof appWin.webkit?.messageHandlers?.bukHybrid?.postMessage ===
      'function';

    return hasAndroidBridge || hasIosBridge;
  }

  private _openExternalPageWithAppBridge(redirectTo: string): void {
    if (this._isExternalPageOpening) {
      return;
    }

    this._isExternalPageOpening = true;

    if (!this._shouldUseAppTokenRedirect() || !this.checkBridgeAvailable()) {
      this._isExternalPageOpening = false;
      window.open(redirectTo, '_blank');
      return;
    }

    this._apiService.getAppToken().subscribe({
      next: (result) => {
        const appToken = result.app_token;
        const redirectUrl = `${
          environment.serverOrigin
        }/api/v1/app/redirect?app_token=${encodeURIComponent(
          appToken
        )}&redirect_to=${encodeURIComponent(redirectTo)}`;

        this._isExternalPageOpening = false;

        const appWindow = window as Window & {
          bukAndroid?: {
            openBrowser?: (url: string) => void;
          };
          webkit?: {
            messageHandlers?: {
              bukHybrid?: {
                postMessage?: (payload: { type: string; data: string }) => void;
              };
            };
          };
        };
        if (typeof appWindow.bukAndroid?.openBrowser === 'function') {
          appWindow.bukAndroid.openBrowser(redirectUrl);
        } else if (appWindow.webkit?.messageHandlers?.bukHybrid?.postMessage) {
          appWindow.webkit.messageHandlers.bukHybrid.postMessage({
            type: 'openBrowser',
            data: redirectUrl,
          });
        } else {
          window.open(redirectUrl, '_blank');
        }
      },
      error: () => {
        this._isExternalPageOpening = false;
      },
    });
  }

  private _shouldUseAppTokenRedirect(): boolean {
    if (!this._isApp) {
      return false;
    }

    const appVersion = this._nativeEnvironmentService.version;
    if (!appVersion) {
      return false;
    }

    const minRequiredVersion =
      this._nativeEnvironmentService.os === 'ios'
        ? environment.minRequiredIOSVersion
        : environment.minRequiredAndroidVersion;

    return !lt(appVersion, minRequiredVersion);
  }
}
